<i18n>
{
	"en": {
		"deviationNumber": "Deviation No.",
		"eventType": "Event Type",
		"deviationType": "Deviation Type",
		"deviationCause": "Deviation Cause",
		"discoveredAt": "Discovered At",
		"terminal": "Terminal",
		"date": "Date",
		"user": "User",
		"licencePlate": "For package",
		"message": "Description",
		"pictures": "Pictures"
	},
	"no": {
		"deviationNumber": "Avviksnr.",
		"eventType": "Hendelsestype",
		"deviationType": "Avvikstype",
		"deviationCause": "Årsak",
		"discoveredAt": "Oppdaget ved",
		"terminal": "Terminal",
		"date": "Dato",
		"user": "Bruker",
		"licencePlate": "Gjelder kolli",
		"message": "Beskrivelse",
		"pictures": "Bilder"
	}
}
</i18n>

<template>
	<t-grid
		:items="deviations"
		:columnDefinitions="columnDefinitions"
		:defaultColumnDefinition="defaultColumnDefinition"
		:rowHeight="40"
		:showFilters="false"
		class="deviations-content"
		selectable
		v-bind="$attrs"
		v-on="$listeners"
	>

		<template v-slot:status="{ value }">
			<span :class="`status _${value.id}`">{{ value[$i18n.locale]}}</span>
		</template>

		<template v-slot:icon="{ item }">
			<t-timpex-icon
				:icon="getIcon(item.eventType)"
				type="bp"
				color="blue"
				style="padding-left: 3px;"
			/>
		</template>

		<template v-slot:deviationNumber="{ item }">
			{{item.deviationNumber}}
		</template>
		<template v-slot:eventType="{ item }">
			{{item.eventType}}
		</template>
		<template v-slot:deviationType="{ item }">
			{{ item.deviationType.code }}
		</template>
		<template v-slot:deviationCause="{ item }">
			{{ item.deviationCause.code }}
		</template>
		<template v-slot:deviationDiscoveredAt="{ item }">
			{{ $t(item.discoveredAt) }}
		</template>
		<template v-slot:terminal="{ displayValue }">
			<div v-if="displayValue" style="height: 16px;">{{ displayValue.name }}</div>
			<div v-if="displayValue" class="grid-cell-sub-line" :class="{'missing-geo-data': !displayValue.latitude || !displayValue.longitude }">{{ displayValue.street }}, {{ displayValue.postalCode }} {{ displayValue.postalPlace }}</div>
		</template>
		<template v-slot:message="{ item }">
			{{ item.message }}
		</template>
		<template v-slot:date="{ value }">
			{{getDate(value)}}
		</template>

		<template v-slot:clerk="{ displayValue }">
			<div v-if="displayValue" style="height: 16px;">
				<template v-if="displayValue.name">
					{{displayValue.name}}
				</template>
				<template v-else-if="displayValue.email">
					{{displayValue.email}}
				</template>
				<template v-else-if="displayValue.phoneNumber">
					{{displayValue.phoneNumber}}
				</template>
			</div>
			<div v-if="displayValue && displayValue.name" class="grid-cell-sub-line">
				<template v-if="displayValue.email">
					{{displayValue.email}}
				</template>
				<template v-else-if="displayValue.phoneNumber">
					{{displayValue.phoneNumber}}
				</template>
			</div>
		</template>

		<template v-slot:licencePlate="{ item }">
			<span v-for="plate of item.licencePlates" :key="plate" class="waybill-number">{{plate}}</span>
		</template>
		<template v-slot:pictures="{ item }">
			<t-icon-button
				icon="media"
				type="bp"
				appearance="minimal"
				disableBoxShadow
				:disabled="deviationHasNoPictures(item)"
				@click="openImageViewer(item)"
			/>
		</template>
	</t-grid>
</template>

<script>
import moment from 'moment';
import { eventIcon, storageDateFormat } from '@/core';

export default {
	name: 'DeviationGrid',
	props: {
		deviations: {
			type: Array,
			default() {
				return [];
			},
		},
	},

	data() {
		return {
			defaultColumnDefinition: { resizable: false, },
		};
	},

	computed: {
		columnDefinitions() {
			return [
				{
					field: 'status',
					displayName: 'Status',
					sortable: true,
					comparableValueGetter: item => item.status.id,
					width: 55,
				},
				{
					field: 'icon',
					displayName: '',
					width: 40,
				},
				{
					field: 'deviationNumber',
					displayName: this.$t('deviationNumber'),
					width: 80,
					resizable: true,
					sortable: true,
					sortOrder: 1,
					sortDirection: 'desc',
				},
				{
					field: 'eventType',
					displayName: this.$t('eventType'),
					width: 70,
					resizable: true,
					sortable: true,
				},
				{
					field: 'deviationType',
					displayName: this.$t('deviationType'),
					width: 130,
					sortable: true,
				},
				{
					field: 'deviationCause',
					displayName: this.$t('deviationCause'),
					width: 130,
					sortable: true,
				},
				{
					field: 'discoveredAt',
					displayName: this.$t('discoveredAt'),
					width: 100,
				},
				{
					field: 'terminal',
					valueGetter: item => item.terminal,
					displayName: this.$t('terminal'),
					width: 160,
					resizable: true,
				},
				{
					field: 'message',
					displayName: this.$t('message'),
					minWidth: 80,
					resizable: true,
					sortable: true,
				},
				{
					field: 'date',
					displayName: this.$t('date'),
					width: 120,
					type: 'date',
					sortable: true,
					comparableValueGetter: item => item.date.epoch,
				},
				{
					field: 'clerk',
					valueGetter: item => item.clerk,
					displayName: this.$t('user'),
					width: 150,
				},
				{
					field: 'licencePlate',
					displayName: this.$t('licencePlate'),
					minWidth: 100,
				},
				{
					field: 'pictures',
					displayName: this.$t('pictures'),
					width: 70,
				}
			];
		},
	},

	methods: {
		getIcon(type) {
			return eventIcon.getIcon(type);
		},
		getDate(date) {
			return moment(date.date, storageDateFormat).format('DD.MM.YYYY HH:mm');
		},
		deviationHasNoPictures(deviation) {
			if (!deviation || !deviation.pictures) return true;
			return deviation.pictures.length <= 0;
		},
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.deviations-content {
	width: 100%;
	min-height: 72px;
    margin-bottom: 10px;
}

.status {
	padding: 0px 2px;
	&._0 {
		background: get-color($green, 'light');
	}
	&._10 {
		background: get-color($gray, 'light');
	}
}

.waybill-number {
	@extend %font-families-mono;
	@extend %text-small;

	display: block;
	color: get-color($text, 'muted');
	letter-spacing: 1.2px;
}
</style>
